import * as moment from 'moment-timezone';
import { SettingsDetails } from '../models';

/**
 * PershingPreferenceUtil
 * Class having static methods to work with preference settings
 */
export class PershingPreferenceUtil {
    static readonly US_TIMEZONE = 'America/New_York';
    static readonly SAVE_DATETIME_FORMAT = 'DD-MMM-YYYY hh:mm:ss a'; // moment js time format

    /**
     * Utility method to parse the settings Detail response
     *          for the input functionName and functionType
     * and return the GridSettingsForCurrentView
     *          settingsItem - grid column view settings for the input key
     *                  if key is not provided, 'default' string will be used as the key
     * @param settingsDetails - SettingsDetails object with settings value
     * @param preferenceKey - Name of the attribute to store/retrieve the value. Sting.
     * @param functionName - Name of the feature. String.
     * @param functionType - String. 'ApplicationSettings'.
     */
    static getGridViewSettings(settingsDetails: SettingsDetails, functionName: string, functionType?: string, preferenceKey?: string): any {
        let gridSettingsForCurrentView: any = null;
        const GRID_SETTINGS_VIEW_KEY = 'grid-view-settings';
        const DEFAULT_KEY = 'default';
        /* currently functionType is defaulted to 'ApplicationSettings'
         *      as this is the default type supported by DB
        */
        if (functionName && functionName.trim() !== '') { // Check mandatory functionName for differentiating each feature settings
            if (settingsDetails && settingsDetails.isAvailable) {
                if (settingsDetails.functionName && settingsDetails.functionName === functionName) {
                    if (settingsDetails.settings) {
                        if (settingsDetails.settings[GRID_SETTINGS_VIEW_KEY]) {
                            if (preferenceKey && preferenceKey.trim() !== '') {
                                gridSettingsForCurrentView = settingsDetails.settings[GRID_SETTINGS_VIEW_KEY][preferenceKey];
                            } else {
                                gridSettingsForCurrentView = settingsDetails.settings[GRID_SETTINGS_VIEW_KEY][DEFAULT_KEY];
                            }
                        }
                    }
                }
            }
        }
        return gridSettingsForCurrentView;
    }

    /**
     * Utility method to parse the settings Detail response
     *          for the input functionName and functionType
     * and return the SettingsDetails
     *          SettingsDetails - whole settings Details with prefilled settings inputs
     *
     * @param settingsDetails - SettingsDetails object with settings value
     * @param functionNameValue - Name of the feature. String.
     * @param functionType - String. 'ApplicationSettings'.
     */
    static getPreferenceSettings(settingsDetails: SettingsDetails, functionNameValue: string, functionType?: string) {
        let preferenceSettings: SettingsDetails = null;
        /* currently functionType is defaulted to 'ApplicationSettings'
         *      as this is the default type supported by DB
        */
        if (functionNameValue && functionNameValue.trim() !== '') { // check functionName for differentiating each feature settings
            if (settingsDetails && settingsDetails.isAvailable) {
                if (settingsDetails.functionName && settingsDetails.functionName === functionNameValue) {
                    preferenceSettings = settingsDetails;
                }
            }

            if (!preferenceSettings) {
                // default settings to pass to pershing grid controller - settings will be used to "firstime -> save" of the component.
                preferenceSettings = {settings : null, functionName : functionNameValue,
                            functionType : functionType != null ? functionType  : 'ApplicationSettings',
                            dataSize: null, isAvailable: false,
                            taskName: null, uniqueId: null, updatedTimeStamp: null } as SettingsDetails;
            }
        }
        return preferenceSettings;
    }

    /**
     * Method to apply preferences to the default grid columns definition
     * @param columnDefinition - ColDef
     * @param columnPreferences - Options chosen by user
     */
    static applyColumnPreferences(columnDefinition: any, columnPreferences: any) {
        if (columnDefinition && columnPreferences) {
            try {
                // get column unique ids from columnPreferences
                const orderedPrefColIds = columnPreferences.map ( (preference) => {
                    return preference.colId;
                });

                // filter the default columns from feature with preferences
                // then apply visibility
                /*
                 if default column list contains columns other than the saved list, they
                 will be still available in the column list but their visibility will be set to "hidden"
                */
                const filteredColSet = columnDefinition.filter((col) => {
                    let colMatcherResult = false;
                    colMatcherResult = columnPreferences.some((preference) => {
                        const colUniqueId  = col.colId ? col.colId : col.field;
                        if (colUniqueId === preference.colId) {
                            col.hide = !preference.visibility;
                            return true;
                        } else {
                            return false;
                        }
                    });
                    /* still we need extra columns (if newly added in feature) from the default column definition
                        but they will be hidden and user can enable it in column organizer.

                        todo -
                        if the new columns added in feature will be pinned columns, grid-controller needs to enable
                        the option to hide/show the newly pinned column in the column organizer, but currently pinned
                        columns are non-editable.
                    */
                    if (!colMatcherResult) {
                        col.hide = true;
                        colMatcherResult = !colMatcherResult;
                    }
                    return colMatcherResult;
                });
                // sort the columns in the order of prefereces
                filteredColSet.sort( (col1, col2) => {
                    const col1UniqueId = col1.colId ? col1.colId : col1.field;
                    const col2UniqueId = col2.colId ? col2.colId : col2.field;
                    return orderedPrefColIds.indexOf(col1UniqueId) - orderedPrefColIds.indexOf(col2UniqueId);
                });
                return filteredColSet;
            } catch (e) {
                // console.log('Error in applying preferences to grid column settings : ', e); // replace with logger
                return columnDefinition;
            }
        } else {
            // console.log('Either Grid settings or preferences un-available'); // replace with logger
            return columnDefinition;
        }
    }

    /**
     * method to prepare columnState from the current grid state
     *
     * @param columnState - State of the column
     * @returns - Value to save
     */
    static prepareColumnStateToSave(columnState) {
        /* Final colState will be in the format of
            Array of ColumnState objects
                    column state will be object - currently having colId (columnUniqueId) and
                    visibility (show/hide status)
                    can be extended in future to store other column state such as width etc.,
        */
        const columnStateToSave = columnState.map(column => {
            const colState = {colId: column.colId, visibility: !column.hide};
            return colState;
         });
        return columnStateToSave;
    }

    /**
     * method to prepare gridColumnSettings before save
     *
     * @param settingsDetails - settingsDetails
     * @param preferenceViewKey - preferenceViewKey
     * @param columnState - columnState
     * @returns settings
     */
    static packGridSettings(settingsDetails: SettingsDetails, preferenceViewKey: string, columnState: any): any {
        const GRID_SETTINGS_VIEW_KEY = 'grid-view-settings';
        const DEFAULT_KEY = 'default';
        let prefSettings = settingsDetails.settings;
        /*  Below will be the grid settings format
         *    {
         *		'grid-view-settings' :
         *		  {
         *           'default' : [{colId:'col1', visibility : false}, {colId:'col2', visibility : false}]
         *           'account' : [{colId:'col1', visibility : false}]
         *			 'account-transaction-view' : [{colId:'col1', visibility : false}, {colId:'col2', visibility : false}, ..],
         *			  ....
         *         }
         *     }
        */
        if (!prefSettings || prefSettings == null) { // if no initial settings available - first time save
            prefSettings = ({}) as any;
            prefSettings[GRID_SETTINGS_VIEW_KEY] = ({}) as any;
        } else {
            if (! prefSettings[GRID_SETTINGS_VIEW_KEY]) {
                prefSettings[GRID_SETTINGS_VIEW_KEY] = ({}) as any;
            }
        }
        const prefKey: string = (preferenceViewKey && preferenceViewKey.trim() !== '') ? preferenceViewKey : DEFAULT_KEY;
        prefSettings[GRID_SETTINGS_VIEW_KEY][prefKey] = columnState;
        return prefSettings;
    }

    /**
     * method to get current datetime stamp
     *        uses moment js to generate and format the current datetime
     *        timezone defaulted to NEW_YORK time zone
     *        output date format (moment js time format) - DD-MMM-YYYY hh:mm:ss a
     *        return datetime string in uppercase
     *              sample output : 08-APR-2019 04:54:08 AM
     *
     * currently DB settings service needs timestamp input in the above format
     */
    static getCurrentTimestamp() {
        const currentDate: Date = new Date();
        const currentDateTimeString = moment.tz(currentDate, PershingPreferenceUtil.US_TIMEZONE).
                                                    format(PershingPreferenceUtil.SAVE_DATETIME_FORMAT);
        return currentDateTimeString.toUpperCase();
    }
}
